import React from 'react';
import styled from '@emotion/styled';
import { Alert } from 'react-bootstrap';

import api from 'api';
import useMountEffect from 'hooks/useMountEffect';
import useReports from 'hooks/useReports';
import useRequest from 'hooks/useRequest';
import useUpdateEffect from 'hooks/useUpdateEffect';

import Loader from 'components/elements/Loader';

const FrameWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-height: 0;

  iframe {
    flex: 1;
    width: 100%;
    min-height: 0;
    border: none;
  }
`;

interface ReportDisplayProps {
  reportId: string;
}

interface ReportData {
  url?: string;
  name?: string;
}

export default function ReportDisplay({ reportId }: ReportDisplayProps) {
  const { impersonateUserId } = useReports();

  // Adjust the generic to your API response type if known
  const {
    data,
    doRequest,
    isLoading,
    error,
  } = useRequest(api.getDashboard);

  const reportData = data === null ? undefined : (data as ReportData | undefined);

  const fetchRequest = React.useCallback(
    () => doRequest(reportId, { impersonateUserId }),
    [doRequest, impersonateUserId, reportId],
  );

  useMountEffect(fetchRequest);

  useUpdateEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  if (isLoading) return <Loader />;

  if (error) return <Alert variant="danger">Error loading report</Alert>;

  if (!reportData?.url?.startsWith('http')) return <Alert variant="warning">Invalid url</Alert>;

  return (
    <FrameWrapper>
      <iframe title={reportData?.name ?? 'Report'} src={reportData?.url} />
    </FrameWrapper>
  );
}
